import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Image, X, Check, Loader2, ArrowRight, Lock, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["Consumer Goods", "Technology", "Fashion", "Food & Beverage", "Automotive", "Finance", "Healthcare", "Entertainment", "Real Estate", "Other"];
const platforms = ["Instagram Feed", "Instagram Stories", "Facebook Feed", "TikTok", "YouTube Thumbnail", "Google Display", "LinkedIn", "Print", "OOH/Billboard", "Other"];
const objectives = ["Brand Awareness", "Engagement", "Traffic/Clicks", "Conversions/Sales", "App Install", "Lead Generation"];

const PLAN_LIMITS = { free: 0, trial: 2, starter: 5, growth: 20, agency: 60, enterprise: 9999 };

export default function NewAnalysisPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get("parent");
  const [step, setStep] = useState(1);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [adName, setAdName] = useState("");
  const [category, setCategory] = useState("");
  const [platform, setPlatform] = useState("");
  const [objective, setObjective] = useState("");
  const [analysisId, setAnalysisId] = useState(null);
  const [error, setError] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [parentAnalysis, setParentAnalysis] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const userPlan = user?.plan || "free";
  const planLimit = PLAN_LIMITS[userPlan] || 0;
  const needsPlan = planLimit === 0;

  // If parent ID is in URL, fetch parent data and prefill form
  useEffect(() => {
    if (!parentId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/analyses/${parentId}`, { withCredentials: true });
        setParentAnalysis(data);
        setCategory(data.category || "");
        setPlatform(data.platform || "");
        setObjective(data.objective || "");
        setAdName(`${data.ad_name} (optimized)`);
      } catch (err) {
        console.error("Failed to fetch parent analysis", err);
      }
    })();
  }, [parentId]);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError("File too large. Max 10MB."); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { setError("Unsupported format. Use JPG, PNG, or WebP."); return; }
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setImageBase64(e.target.result);
    };
    reader.readAsDataURL(file);
    if (!adName) setAdName(file.name.replace(/\.[^.]+$/, ""));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const [revisionSuggestion, setRevisionSuggestion] = useState(null);

  const handleSubmit = async () => {
    if (needsPlan) { setShowUpgrade(true); return; }
    if (!imageBase64 || !adName || !category || !platform || !objective) {
      setError("Please fill all fields and upload an image.");
      return;
    }
    setStep(2);
    try {
      const payload = {
        ad_name: adName, category, platform, objective,
        image_base64: imageBase64,
      };
      if (parentId) payload.parent_analysis_id = parentId;
      const { data } = await axios.post(`${API}/analyses`, payload, { withCredentials: true });
      setAnalysisId(data.id);
      // If backend suggests this could be a revision (and user did NOT come from "Analyze Optimized" flow), show pop-up
      if (data.possible_revision && !parentId) {
        setRevisionSuggestion({
          analysisId: data.id,
          parentId: data.possible_revision.suggested_parent_id,
          parentName: data.possible_revision.suggested_parent_name,
          parentScore: data.possible_revision.suggested_parent_score,
        });
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail === "no_plan") {
        setShowUpgrade(true);
        setStep(1);
      } else if (detail.startsWith("limit_reached")) {
        setError(`You've reached your monthly limit of ${detail.split(":")[1]} analyses. Upgrade your plan for more.`);
        setShowUpgrade(true);
        setStep(1);
      } else {
        setError("Failed to start analysis. " + detail);
        setStep(1);
      }
    }
  };

  const confirmRevision = async () => {
    if (!revisionSuggestion) return;
    try {
      await axios.put(`${API}/analyses/${revisionSuggestion.analysisId}/mark-as-revision`,
        { parent_analysis_id: revisionSuggestion.parentId },
        { withCredentials: true });
    } catch (err) {
      console.error("Failed to mark as revision", err);
    }
    setRevisionSuggestion(null);
  };

  const dismissRevision = () => setRevisionSuggestion(null);

  // Upgrade prompt for free users
  if (needsPlan || showUpgrade) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="upgrade-prompt">
        <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-6">
          <Lock size={28} className="text-orange-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Choose a plan to start analyzing
        </h2>
        <p className="text-sm text-slate-500 mb-8 max-w-sm mx-auto">
          Select a plan to unlock more analyses powered by neuroscience and AI.
        </p>
        <div className="space-y-3">
          {[
            { name: "Starter", price: "$39/mo", analyses: "5 analyses/month" },
            { name: "Growth", price: "$89/mo", analyses: "20 analyses/month", popular: true },
            { name: "Agency", price: "$219/mo", analyses: "60 analyses/month" },
          ].map(p => (
            <button key={p.name} onClick={() => navigate("/#pricing")}
              className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all ${p.popular ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="text-left">
                <span className="text-sm font-bold text-slate-800">{p.name}</span>
                {p.popular && <span className="ml-2 text-[10px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">Popular</span>}
                <p className="text-xs text-slate-500 mt-0.5">{p.analyses}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-700" style={{ fontFamily: "JetBrains Mono" }}>{p.price}</span>
                <ArrowRight size={14} className="text-slate-400" />
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-6">Cancel anytime.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-analysis-page">
      <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
        {step === 1 ? (parentAnalysis ? "Analyze Optimized Version" : "New Analysis") : "Analyzing Your Ad"}
      </h1>

      {/* Parent banner */}
      {parentAnalysis && step === 1 && (
        <div className="mb-5 p-4 rounded-xl border border-orange-200" style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FDF4FF 100%)" }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} className="text-orange-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800">Optimized Version</p>
              <p className="text-xs text-slate-600 mt-1">
                You're analyzing an optimized version of <span className="font-semibold">{parentAnalysis.ad_name}</span>
                {parentAnalysis.results?.neuro_score && (
                  <> (Score: <span className="font-semibold" style={{ fontFamily: "JetBrains Mono, monospace" }}>{parentAnalysis.results.neuro_score}</span>)</>
                )}
              </p>
            </div>
            <button onClick={() => navigate("/analyze/new")} className="text-xs text-slate-500 hover:text-slate-700 flex-shrink-0">
              Start fresh →
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <UploadStep
          imagePreview={imagePreview}
          adName={adName} setAdName={setAdName}
          category={category} setCategory={setCategory}
          platform={platform} setPlatform={setPlatform}
          objective={objective} setObjective={setObjective}
          error={error}
          onFile={handleFile}
          onDrop={handleDrop}
          fileRef={fileRef}
          onClear={() => { setImagePreview(null); setImageBase64(null); }}
          onSubmit={handleSubmit}
          isOptimized={!!parentAnalysis}
        />
      )}

      {step === 2 && (
        <ProcessingStep imagePreview={imagePreview} analysisId={analysisId} navigate={navigate} />
      )}

      {/* Revision suggestion modal */}
      {revisionSuggestion && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" }}>
              <Sparkles size={20} className="text-white" />
            </div>
            <h3 className="text-lg font-bold text-center mb-2" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
              Is this a revision?
            </h3>
            <p className="text-sm text-slate-600 text-center mb-5">
              We noticed you analyzed a similar ad recently:
              <br />
              <span className="font-semibold text-slate-800">"{revisionSuggestion.parentName}"</span>
              {revisionSuggestion.parentScore && (
                <> with score <span style={{ fontFamily: "JetBrains Mono, monospace" }} className="font-semibold">{revisionSuggestion.parentScore}/100</span></>
              )}
              <br />
              <br />
              Is this an optimized version of that ad? Marking it as a revision lets us compare progress and avoid contradictory recommendations.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={dismissRevision}>
                No, it's separate
              </Button>
              <Button className="flex-1 rounded-xl text-white border-0"
                style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 100%)" }}
                onClick={confirmRevision}>
                Yes, it's a revision
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadStep({ imagePreview, adName, setAdName, category, setCategory, platform, setPlatform, objective, setObjective, error, onFile, onDrop, fileRef, onClear, onSubmit, isOptimized }) {
  return (
    <div className="space-y-5">
      {/* Upload zone */}
      <div
        data-testid="upload-dropzone"
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !imagePreview && fileRef.current?.click()}
        className={`bg-white rounded-2xl border-2 border-dashed ${imagePreview ? "border-slate-200" : "border-slate-300 hover:border-orange-300 cursor-pointer"} p-6 transition-colors`}
      >
        {!imagePreview ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
              <Upload size={28} className="text-orange-500" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1">Drop your ad image here</p>
            <p className="text-xs text-slate-400">JPG, PNG, or WebP — Max 10MB</p>
          </div>
        ) : (
          <div className="relative">
            <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-xl object-contain" />
            <button
              data-testid="clear-image-btn"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-slate-500 hover:text-red-500"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => onFile(e.target.files?.[0])}
          className="hidden"
          data-testid="file-input"
        />
      </div>

      {/* Metadata fields */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1.5 block">Ad Name</label>
          <Input data-testid="ad-name-input" value={adName} onChange={(e) => setAdName(e.target.value)} placeholder="My Campaign Ad" className="rounded-xl h-10" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SelectField testId="category-select" label="Category" value={category} onChange={setCategory} options={categories} />
          <SelectField testId="platform-select" label="Platform" value={platform} onChange={setPlatform} options={platforms} />
          <SelectField testId="objective-select" label="Objective" value={objective} onChange={setObjective} options={objectives} />
        </div>
      </div>

      {error && <p data-testid="upload-error" className="text-sm text-red-500">{error}</p>}

      <Button
        data-testid="run-analysis-btn"
        onClick={onSubmit}
        className="gradient-btn rounded-xl h-12 w-full text-base font-semibold"
      >
        <span className="flex items-center gap-2">{isOptimized ? "Analyze Optimized Version" : "Run Analysis"} <ArrowRight size={18} /></span>
      </Button>
    </div>
  );
}

function SelectField({ testId, label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-500 mb-1.5 block">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
      >
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

const processingSteps = [
  "Analyzing visual composition...",
  "Generating attention heatmap...",
  "Detecting brand elements...",
  "Calculating cognitive load...",
  "Predicting engagement metrics...",
  "Generating recommendations...",
];

function ProcessingStep({ imagePreview, analysisId, navigate }) {
  const [currentStep, setCurrentStep] = useState(-1);
  const [completed, setCompleted] = useState(false);
  const [failed, setFailed] = useState(null);

  // Poll for real progress from backend
  useEffect(() => {
    if (!analysisId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/analyses/${analysisId}`);
        if (data.processing_step !== undefined && data.processing_step > currentStep) {
          setCurrentStep(data.processing_step);
        }
        if (data.status === "completed") {
          setCurrentStep(6);
          setCompleted(true);
          clearInterval(interval);
          setTimeout(() => navigate(`/analyze/${analysisId}`), 1200);
        } else if (data.status === "failed") {
          setFailed(data.error || "Analysis failed. Please try again.");
          clearInterval(interval);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [analysisId, navigate, currentStep]);

  return (
    <div data-testid="processing-view" className="bg-white rounded-2xl border border-slate-200 p-8">
      <div className="max-w-md mx-auto">
        {/* Image with scan effect */}
        {imagePreview && (
          <div className="relative mb-8 rounded-xl overflow-hidden">
            <img src={imagePreview} alt="Analyzing" className="w-full max-h-48 object-contain" />
            <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 to-transparent animate-pulse" />
            {!completed && (
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-orange-500 to-transparent animate-scan" style={{
                  animation: "scanLine 2s ease-in-out infinite"
                }} />
              </div>
            )}
          </div>
        )}

        {/* Steps */}
        <div className="space-y-3">
          {processingSteps.map((step, i) => (
            <div key={i} data-testid={`processing-step-${i}`} className={`flex items-center gap-3 transition-all duration-500 ${i <= currentStep ? "opacity-100" : "opacity-30"}`}>
              {i < currentStep ? (
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check size={12} className="text-emerald-600" />
                </div>
              ) : i === currentStep ? (
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
                  <Loader2 size={12} className="text-orange-600 animate-spin" />
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full bg-slate-100" />
              )}
              <span className={`text-sm ${i < currentStep ? "text-slate-700" : i === currentStep ? "text-orange-700 font-medium" : "text-slate-400"}`}>
                {step}
              </span>
            </div>
          ))}
        </div>

        {completed && (
          <div className="mt-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Check size={20} className="text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-emerald-700">Analysis complete! Redirecting...</p>
          </div>
        )}

        {failed && (
          <div className="mt-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <X size={20} className="text-red-500" />
            </div>
            <p className="text-sm font-medium text-red-600 mb-1">Analysis failed</p>
            <p className="text-xs text-slate-500 mb-4">{failed}</p>
            <Button
              onClick={() => navigate("/analyze/new")}
              className="gradient-btn rounded-xl text-sm font-semibold"
            >
              <span>Try Again</span>
            </Button>
          </div>
        )}

        {!completed && !failed && (
          <p className="text-xs text-slate-400 text-center mt-6">This usually takes a few minutes</p>
        )}
      </div>

      <style>{`
        @keyframes scanLine {
          0% { top: 0; }
          50% { top: 100%; }
          100% { top: 0; }
        }
      `}</style>
    </div>
  );
}
